import { LOCATION_CHANGE, push } from 'react-router-redux';
import type { RemoteData } from '../async-actions';
import { createFetchAction } from '../async-actions';
import { getSession } from '../helpers/Session';
import type {
	GetLocationListingResult,
	LocationListing,
	UpdateRestrictionListLocationLinksResult,
} from '../ogm-hub';
import * as Hub from '../ogm-hub';
import { UpdateRestrictionListLocationLinks } from '../remote/hubs/Web_Controllers_V3_RestrictionsController';
import { getSiteTypeFromId } from './shared';

export type RestrictionLocationAssignmentSuccessState =
	GetLocationListingResult & {
		allChecked: boolean;
		initialLocationMapping: LocationsMap;
		locationMapping: LocationsMap;
	};

type LocationsMap = Record<number, boolean>;

export interface RestrictionLocationAssignmentPageState {
	page: RemoteData<RestrictionLocationAssignmentSuccessState, string[]>;
	listId: number | null;
	isSaving: boolean;
	siteType: 'sysco' | 'dssi' | null;
	title: string | null;
}

export function initialState(): RestrictionLocationAssignmentPageState {
	return {
		page: { type: 'initial' },
		listId: null,
		isSaving: false,
		siteType: null,
		title: null,
	};
}

export function RestrictionLocationReducer(
	state: RestrictionLocationAssignmentPageState = initialState(),
	action: LocationAction
): RestrictionLocationAssignmentPageState {
	const { page } = state;
	if (page.type === 'initial') {
		switch (action.type) {
			case '@restr-locations/LOCATION_ASSIGNMENT_PAGE_PENDING': {
				return {
					...initialState(),
					listId: action.payload.listId,
					siteType: getSiteTypeFromId(action.payload.siteTypeId),
					page: { type: 'loading' },
				};
			}
		}
	}

	if (page.type === 'loading') {
		switch (action.type) {
			case '@restr-locations/LOCATION_ASSIGNMENT_PAGE_FAILURE': {
				return {
					...state,
					page: { type: 'failure', err: action.errors },
				};
			}

			case '@restr-locations/LOCATION_ASSIGNMENT_PAGE_SUCCESS': {
				const { supplierName, restrictionListName, locations } = action.payload;
				const allChecked = locations.every((x) => x.isSelected);
				const locationMapping = CalculateLocationMapping(locations);
				const initialLocationMapping = CalculateLocationMapping(locations);
				const title =
					state.siteType === 'dssi'
						? `${supplierName} - ${restrictionListName}`
						: restrictionListName;

				const data = {
					...action.payload,
					allChecked,
					locationMapping,
					initialLocationMapping,
				};
				return {
					...state,
					title,
					page: { type: 'success', data },
				};
			}
		}
	}

	if (page.type === 'success') {
		switch (action.type) {
			case '@restr-locations/CHECK_LOCATION': {
				const { locations, initialLocationMapping } = page.data;
				locations.forEach((location, index) => {
					if (location.locationId === action.payload.locationId) {
						if (
							!(
								state.siteType === 'sysco' &&
								locations[index].isSelected &&
								initialLocationMapping[location.locationId]
							)
						) {
							locations[index] = FlipCheckedValue(locations[index]);
						}
					}
				});
				const locationMapping = CalculateLocationMapping(locations);
				const allChecked = locations.every((x) => x.isSelected);
				const data = { ...page.data, allChecked, locations, locationMapping };

				return {
					...state,
					page: { type: 'success', data },
				};
			}
			case '@restr-locations/CHECK_ALL': {
				const { locations, initialLocationMapping } = page.data;
				const nextAllChecked = !page.data.allChecked;
				locations.forEach((location, index) => {
					if (
						nextAllChecked ||
						!(
							state.siteType === 'sysco' &&
							initialLocationMapping[location.locationId]
						)
					) {
						locations[index].isSelected = nextAllChecked;
					}
				});
				const locationMapping = CalculateLocationMapping(locations);
				const data = {
					...page.data,
					allChecked: nextAllChecked,
					locations,
					locationMapping,
				};

				return {
					...state,
					page: { type: 'success', data },
				};
			}
			case '@restr-locations/SAVE_LOCATION_PENDING': {
				return {
					...state,
					isSaving: true,
				};
			}
			case '@restr-locations/SAVE_LOCATION_FAILURE': {
				return {
					...state,
					isSaving: false,
				};
			}
			case '@restr-locations/SAVE_LOCATION_SUCCESS': {
				return {
					...state,
					isSaving: false,
				};
			}
			case LOCATION_CHANGE: {
				return initialState();
			}
		}
	}
	return state;
}

function FlipCheckedValue(listing: LocationListing): LocationListing {
	listing.isSelected = !listing.isSelected;
	return listing;
}

function CalculateLocationMapping(locations: LocationListing[]): LocationsMap {
	const locationMapping: LocationsMap = {};
	locations.forEach((location) => {
		locationMapping[location.locationId] = location.isSelected;
	});

	return locationMapping;
}

export type LocationAction =
	| LOCATION_ASSIGNMENT_PAGE_PENDING
	| LOCATION_ASSIGNMENT_PAGE_FAILURE
	| LOCATION_ASSIGNMENT_PAGE_SUCCESS
	| CHECK_ALL
	| CHECK_LOCATION
	| SAVE_LOCATION_PENDING
	| SAVE_LOCATION_SUCCESS
	| SAVE_LOCATION_FAILURE;

export interface LOCATION_ASSIGNMENT_PAGE_PENDING {
	type: '@restr-locations/LOCATION_ASSIGNMENT_PAGE_PENDING';
	payload: { listId: number; siteTypeId: number };
}

export interface LOCATION_ASSIGNMENT_PAGE_FAILURE {
	type: '@restr-locations/LOCATION_ASSIGNMENT_PAGE_FAILURE';
	errors: string[];
}

export interface LOCATION_ASSIGNMENT_PAGE_SUCCESS {
	type: '@restr-locations/LOCATION_ASSIGNMENT_PAGE_SUCCESS';
	payload: GetLocationListingResult;
}

export const locationAssignmentPageLoaded = createFetchAction<
	number,
	LOCATION_ASSIGNMENT_PAGE_PENDING,
	LOCATION_ASSIGNMENT_PAGE_FAILURE,
	LOCATION_ASSIGNMENT_PAGE_SUCCESS
>(
	'@restr-locations/LOCATION_ASSIGNMENT_PAGE_PENDING',
	'@restr-locations/LOCATION_ASSIGNMENT_PAGE_FAILURE',
	'@restr-locations/LOCATION_ASSIGNMENT_PAGE_SUCCESS',
	(listId) => Hub.RestrictionsController.GetLocationListing(listId),
	(listId) => {
		const { siteTypeId } = getSession();
		return {
			listId,
			siteTypeId,
		};
	}
);

export interface SAVE_LOCATION_PENDING {
	type: '@restr-locations/SAVE_LOCATION_PENDING';
}

export interface SAVE_LOCATION_FAILURE {
	type: '@restr-locations/SAVE_LOCATION_FAILURE';
	errors: string[];
}

export interface SAVE_LOCATION_SUCCESS {
	type: '@restr-locations/SAVE_LOCATION_SUCCESS';
	payload: UpdateRestrictionListLocationLinksResult;
}

export const saveLocationAssignment = createFetchAction<
	{},
	SAVE_LOCATION_PENDING,
	SAVE_LOCATION_FAILURE,
	SAVE_LOCATION_SUCCESS
>(
	'@restr-locations/SAVE_LOCATION_PENDING',
	'@restr-locations/SAVE_LOCATION_FAILURE',
	'@restr-locations/SAVE_LOCATION_SUCCESS',
	(args, dispatch, getState) => {
		const { restrictionLocationAssignment } = getState();

		const locationIds =
			restrictionLocationAssignment.page.type === 'success'
				? restrictionLocationAssignment.page.data.locations
						.filter((location) => location.isSelected)
						.map((loc) => loc.locationId)
				: null;

		const restrictionListId = restrictionLocationAssignment.listId;

		if (!restrictionListId) {
			throw new Error('Tried to save on a null restriction list.');
		}

		return UpdateRestrictionListLocationLinks({
			restrictionListId,
			locationIds,
		}).then((res) => {
			if (res.ok) {
				dispatch(push('/restrictions'));
			}

			return res;
		});
	}
);

export interface CHECK_ALL {
	type: '@restr-locations/CHECK_ALL';
}

export function checkAllLocations(): CHECK_ALL {
	return {
		type: '@restr-locations/CHECK_ALL',
	};
}

export interface CHECK_LOCATION {
	type: '@restr-locations/CHECK_LOCATION';
	payload: { locationId: number };
}

export function checkLocation(locationId: number): CHECK_LOCATION {
	return {
		type: '@restr-locations/CHECK_LOCATION',
		payload: { locationId },
	};
}
